// Sustemia · Balance Hídrico · datos de demostración
// Portado de PLANTA.A_TELAS / MEDIDORES / PROCESOS_PLANTA / TANQUE_PLANTA en
// Sustemia_BalanceHidrico_Modulo_ES_v1_2026-08-11.html, y de los ejemplos de
// los casos A1/A2 de la Guía de campo (los mismos números que ya cubren los
// tests del motor).
//
// El modo "Planta de ejemplo" reutiliza el wizard completo de "Mi planta"
// (WaterBalanceWorkspace) con estos datos ya cargados, en vez de reconstruir
// las siete pantallas propias que tenía el demo del prototipo original (Qué
// falta / Guías de campo / Cómo cierra / Explicar la diferencia / Revisar /
// Informe / Cambiar planta) — son vistas bastante distintas entre sí y el
// contenido de fondo es el mismo balance. "Cambiar planta" equivale acá a
// simplemente elegir otro modo en el selector.
//
// `localSeed` usa los mismos nombres de campo en inglés que estado.js (el
// documento real). Los valores de catálogo (categorías, orígenes) y el
// texto narrativo siguen en español, igual que en el resto del módulo.
//
// DATOS DE DEMOSTRACIÓN. Ver Fuentes.md: los números son ficticios: los
// métodos, grados y fuentes que representan (M36, DOE/FEMP BMP#10) no.

const AHORA = new Date().toISOString();
const CALIBRACION_VIGENTE = new Date(Date.now() + 1000 * 60 * 60 * 24 * 365).toISOString().slice(0, 10);

export function construirDemoATelas() {
  const waterDoc = {
    groundwater_production: {
      value: 186400,
      unit: "m3",
      records: [{ source: "manual" }],
      files: [{ uniqueName: "demo-permiso-pozo.pdf", name: "Permiso de extracción", url: "" }],
      updatedAt: AHORA,
    },
    municipal_network_water_production: {
      value: 24000,
      unit: "m3",
      records: [{ source: "invoice_reader" }],
      files: [{ uniqueName: "demo-factura-red.pdf", name: "Factura del prestador", url: "" }],
      updatedAt: AHORA,
    },
  };

  const productionDoc = { production_volume: { value: 2400000, unit: "kg de tela acabada" } };

  const socialDoc = {
    days_worked: { value: 310 },
    female_production_workers: { value: 200 },
    male_production_workers: { value: 180 },
    female_administrative_workers: { value: 20 },
    male_administrative_workers: { value: 20 },
  };

  const localSeed = {
    typology: "A_TELAS",
    hasLaundry: true,
    context: {
      geoNote: "Guatemala · cuenca Motagua",
      wriRisk: "Alto (3-4)",
      wwfRisk: "Medio-alto",
    },
    sourceAnnotations: [
      { sourceId: "groundwater_production", declaredMethod: "medidor", calibrationDue: CALIBRACION_VIGENTE },
      { sourceId: "municipal_network_water_production", declaredMethod: "factura" },
    ],
    centers: [
      { id: "pozo", nombre: "Pozo propio", recibe: "—", descarga: "Red interna", fluido: "limpia" },
      { id: "tintoreria", nombre: "Tintorería y acabado", recibe: "Colector de agua tratada", descarga: "Planta de tratamiento", fluido: "proceso" },
      { id: "torre_enfriamiento", nombre: "Torre de enfriamiento", recibe: "Colector general", descarga: "Evaporación al aire", fluido: "limpia" },
      { id: "etp", nombre: "Planta de tratamiento (ETP)", recibe: "Todos los efluentes", descarga: "Colectora o cuerpo receptor", fluido: "efluente" },
    ],
    instruments: [
      { id: "inst_pozo", name: "Pozo profundo", instrumentType: "turbina", fluid: "limpia", meterRole: "entrada", calibrationDue: CALIBRACION_VIGENTE },
      { id: "inst_descarga_etp", name: "Descarga a planta de tratamiento", instrumentType: "electro", fluid: "efluente", meterRole: "salida", calibrationDue: CALIBRACION_VIGENTE },
      { id: "inst_reposicion_torre", name: "Reposición de torre", instrumentType: "turbina", fluid: "limpia", meterRole: "interno", calibrationDue: CALIBRACION_VIGENTE },
      { id: "inst_purga_torre", name: "Purga de torre", instrumentType: "turbina", fluid: "limpia", meterRole: "interno", calibrationDue: CALIBRACION_VIGENTE },
    ],
    outputs: [
      {
        centerId: "etp",
        name: "Efluente a planta de tratamiento",
        origin: "medidor",
        instrumentId: "inst_descarga_etp",
        volumeM3: 142000,
        validityGrade: 5,
        evidence: [{ uniqueName: "demo-efluente.pdf", name: "Lectura del medidor", url: "" }],
      },
      {
        centerId: "torre_enfriamiento",
        name: "Evaporación en torre de enfriamiento",
        origin: "compartido",
        volumeM3: 18700,
        validityGrade: 3,
        calculationMemo: { reposicionMedida: 24600, purgaMedida: 5900, ciclosDeConcentracion: 4.17 },
        evidence: [],
      },
    ],
    storage: [
      { name: "Cisterna principal", geometry: { diameter: 10 }, initialLevel: 5, finalLevel: 20.28 }, // Δ ≈ +1.200 m³
    ],
    executedFieldCases: [
      {
        caseId: "A1_POZO_MACROMEDIDOR",
        date: AHORA,
        responsible: "mantenimiento, sustentabilidad",
        data: { volumenM3: 186400, usoDelPermisoPct: 93.2 },
        gradeAwarded: 5,
        durationMin: 5,
        evidence: [{ uniqueName: "demo-lectura-pozo.pdf", name: "Foto del totalizador", url: "" }],
      },
      {
        caseId: "A2_TORRE_POR_DIFERENCIA",
        date: AHORA,
        responsible: "mantenimiento",
        data: { reposicionMedida: 24600, purgaMedida: 5900, evaporacionM3: 18700, ciclosDeConcentracion: 4.17 },
        gradeAwarded: 3,
        durationMin: 10,
        evidence: [],
      },
    ],
    // El descuadre da ~48.500 m³ (23%): a propósito no cierra perfecto — un
    // demo que cierra en 0% no enseña nada sobre para qué sirve la
    // clasificación M36. Se clasifica la mayor parte y se deja un resto
    // "todavía no lo identificamos", como en un caso real.
    lossClassification: [
      { category: "real", volumeM3: 20000, note: "Fuga estimada en red interna, en investigación" },
      { category: "aparente", volumeM3: 15000, note: "Posible error de calibración en M04" },
      { category: "autorizado", volumeM3: 8000, note: "Limpieza de planta y pruebas hidráulicas" },
      { category: "sin_identificar", volumeM3: 5500, note: "" },
    ],
    correctionProposals: [],
    divergences: [],
    costs: {
      tariffs: { waterPerM3: 0.8, thermalEnergyPerM3: 0.3, chemicalsPerM3: 0.5, treatmentPerM3: 0.4 },
      perCenter: [],
    },
    closed: true,
  };

  return { waterDoc, productionDoc, socialDoc, localSeed };
}

export default { construirDemoATelas };
